// Cross-source context — fetched once at AppV2 mount, consumed by all tabs.
// Each source is a Set<string> of ticker symbols flagged by that source today.

import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

export const SourcesContext = createContext({
  premarket: new Set(),
  reddit: new Set(),
  stocktwits: new Set(),
  trending: new Set(),
  loaded: false,
});

export function useSources() {
  return useContext(SourcesContext);
}

// Get the flag vector [PM, Reddit, StockTwits, Trending] for a given ticker
export function sourcesFor(sources, ticker) {
  if (!sources || !sources.loaded) return [false, false, false, false];
  return [
    sources.premarket.has(ticker),
    sources.reddit.has(ticker),
    sources.stocktwits.has(ticker),
    sources.trending.has(ticker),
  ];
}

export function SourcesProvider({ children }) {
  const [sources, setSources] = useState({
    premarket: new Set(),
    reddit: new Set(),
    stocktwits: new Set(),
    trending: new Set(),
    loaded: false,
  });

  useEffect(() => {
    (async () => {
      const [scansRes, wsbRes, buzzRes, monstersRes] = await Promise.allSettled([
        axios.get(`${API_URL}/api/scans`),
        axios.get(`${API_URL}/api/wsb-portfolio`),
        axios.get(`${API_URL}/api/buzz-scans`),
        axios.get(`${API_URL}/api/monsters`),
      ]);

      const premarket = new Set();
      if (scansRes.status === 'fulfilled') {
        const list = scansRes.value.data || [];
        if (list.length) {
          // Fetch the latest scan
          try {
            const latest = list.sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''))[0];
            const r = await axios.get(`${API_URL}/api/scans/${encodeURIComponent(latest.id)}`);
            const tm = r.data?.top_movers || {};
            Object.values(tm).forEach((arr) => {
              if (Array.isArray(arr)) arr.forEach((t) => t.symbol && premarket.add(t.symbol));
            });
          } catch {}
        }
      }

      const reddit = new Set();
      if (wsbRes.status === 'fulfilled') {
        (wsbRes.value.data || []).forEach((t) => t.symbol && reddit.add(t.symbol));
      }

      const stocktwits = new Set();
      if (buzzRes.status === 'fulfilled') {
        const latest = buzzRes.value.data?.latest;
        const tb = latest?.top_buzz || [];
        tb.forEach((t) => t.symbol && stocktwits.add(t.symbol));
      }

      const trending = new Set();
      if (monstersRes.status === 'fulfilled') {
        const lp = monstersRes.value.data?.live_picks || [];
        lp.forEach((p) => p.ticker && trending.add(p.ticker));
      }

      setSources({ premarket, reddit, stocktwits, trending, loaded: true });
    })();
  }, []);

  return <SourcesContext.Provider value={sources}>{children}</SourcesContext.Provider>;
}
